import {
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { removeBetSlipEvent } from 'utils/betslip/betslip';
import LS from 'utils/bom-dom-manipulation/local-storage';
import {
  COUNTER_OFFER_ACTIVE_TYPE,
  SUPER_BET_ACTIVE_TYPE
} from 'utils/constants/app/magic-numbers-app';
import { BET_TYPES } from 'utils/constants/betslip/bet-types';
import { ERROR_CODE_TIMEOUT_1800 } from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { showToastError } from 'utils/generic/show-toast-error';
import { showToastWarning } from 'utils/generic/show-toast-warning';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { TPopover } from 'interfaces/betslip';
import { PartnerConfig } from 'interfaces/partner-config';
import { useOpenLoginPopup } from './account/useOpenLoginPopup';
import { useDoBetHandler } from 'hooks/betslip/useDoBet';
import {
  setQuickBetSelectedEventId,
  setQuickSingleBetslipOpen,
  triggerBetted,
  updateQuickBetInProcessCount
} from 'store/actions/betslip';
import {
  getAllLsBets,
  getBetsCalculations,
  getCounterOfferActiveEventIds,
  getEditBet,
  getQuickBetEnabled,
  getQuickBetStake,
  getShouldShowQuickBetInfoPopover,
  getSuperBetActiveEventIds
} from 'store/selectors/betslip';
import { getPartnerConfigs } from 'store/selectors/socket';
import { getIsLoggedIn } from 'store/selectors/user-data';

export type TOddChangeHandler = (
  eventId: string,
  price: number,
  sp?: boolean,
  prevSp?: boolean,
  sendPostMessageToIframeSportsbook?: () => void
) => void;

export type TOddChange = {
  oddChangeHandler: TOddChangeHandler;
  popover: TPopover;
  hidePopover: () => void;
};

export const useOddChange = (
  setSelectedEvents: Dispatch<SetStateAction<string[]>>,
  addToBetSlip: (eventId: string | number, sp?: boolean) => void,
  selectedEvents: string[]
): TOddChange => {
  const [popover, setPopover] = useState<TPopover>(null);
  const calculation = useSelector(getBetsCalculations);

  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const history = useHistory();
  const { t } = useTranslation();
  const quickBetEnabled = useSelector(getQuickBetEnabled);
  const shouldShowQuickBetInfoPopover = useSelector(
    getShouldShowQuickBetInfoPopover
  );

  const quickBetStake = useSelector(getQuickBetStake);
  const editBet = useSelector(getEditBet);
  const partnerConfig = useSelector(getPartnerConfigs) as PartnerConfig;
  const superBetEventIds = useSelector(getSuperBetActiveEventIds);
  const counterOfferEventIds = useSelector(getCounterOfferActiveEventIds);

  const allLsBets = useSelector(getAllLsBets);

  const openLoginPopup = useOpenLoginPopup();
  const { doBetHandler } = useDoBetHandler();

  const toggleScroll = useCallback((type: 'enable' | 'disable'): void => {
    if (!isMobile()) {
      const elements = document.querySelectorAll('[data-popover-parent]');

      elements.forEach((element: Element) => {
        if (element instanceof HTMLElement) {
          if (type === 'enable') {
            element.style.overflow = 'auto';
          } else {
            element.style.overflow = 'hidden';
          }
        }
      });
    }
  }, []);

  const handleSubmit = useCallback(
    (event: { id: string; price: number; booking_id?: number }) => {
      if (!isLoggedIn) {
        openLoginPopup();

        return;
      }

      const messageContext = isMobile()
        ? document.body
        : document.getElementById('betslip-wrapper');

      if (event && quickBetStake) {
        // @Todo
        const isCounterOfferActive = counterOfferEventIds.some(
          id => id == event.id
        );

        const isSuperBetActive = superBetEventIds.some(id => id == event.id);
        dispatch(updateQuickBetInProcessCount('increase'));
        doBetHandler(
          {
            amount: Number(quickBetStake),
            bets: [
              {
                event_id: Number(event.id),
                price: Number(event.price),
                booking_id: event.booking_id
              }
            ],
            type: BET_TYPES.SINGLE,
            mode: isCounterOfferActive
              ? COUNTER_OFFER_ACTIVE_TYPE
              : isSuperBetActive || SpringConfigs.SUPER_BET_DEFAULT_ON
              ? SUPER_BET_ACTIVE_TYPE
              : Number(
                  JSON.parse(
                    LS.getItem(storageKeyName('betslip', 'ACCEPT_BET'))
                  ).value
                )
          },
          calculation?.singleEventsCalculations?.[event.id]?.finalResult
            ?.possibleWin || 0,
          (response: { result: object | string | number }) => {
            const { result } = response;
            dispatch(setQuickBetSelectedEventId(''));

            if (result !== 'OK' && result !== ERROR_CODE_TIMEOUT_1800) {
              showToastError(t('betslip.wentWrong'));
              dispatch(updateQuickBetInProcessCount('decrease'));

              return;
            }

            dispatch(updateQuickBetInProcessCount('decrease'));
            setSelectedEvents([]);
            dispatch(triggerBetted());
          },
          () => {
            dispatch(updateQuickBetInProcessCount('decrease'));
            setSelectedEvents([]);

            dispatch(setQuickBetSelectedEventId(''));
          },
          () => {},
          messageContext
        );
      }
    },
    [
      isLoggedIn,
      history,
      quickBetStake,
      setSelectedEvents,
      dispatch,
      t,
      calculation
    ]
  );

  const showPopover = useCallback(
    (eventId: string, price: number) => {
      const onConfirm = (event?: MouseEvent) => {
        event?.stopPropagation();

        handleSubmit({ id: eventId, price });

        if (shouldShowQuickBetInfoPopover) {
          setPopover(null);
          toggleScroll('enable');
        }
      };

      if (shouldShowQuickBetInfoPopover && isLoggedIn) {
        if (Number(quickBetStake)) {
          toggleScroll('disable');

          const onCancel = (event?: SyntheticEvent) => {
            event?.stopPropagation();

            dispatch(setQuickBetSelectedEventId(''));
            setSelectedEvents([]);
            setPopover(null);
            toggleScroll('enable');
          };

          setPopover({
            id: eventId,
            onCancel,
            onConfirm
          });
        }
      } else {
        setPopover({
          id: eventId,
          onCancel: () => {
            dispatch(setQuickBetSelectedEventId(''));
          },
          onConfirm,
          validateRecaptcha: true,
          selectedEvents
        });
      }
    },
    [
      shouldShowQuickBetInfoPopover,
      quickBetStake,
      handleSubmit,
      isLoggedIn,
      toggleScroll,
      t,
      selectedEvents
    ]
  );

  const hidePopover = useCallback(() => {
    if (shouldShowQuickBetInfoPopover) {
      setSelectedEvents([]);
      setPopover(null);
      toggleScroll('enable');
      dispatch(setQuickBetSelectedEventId(''));
    }
  }, [toggleScroll, shouldShowQuickBetInfoPopover]);

  const oddChangeHandler = useCallback<TOddChangeHandler>(
    (eventId, price, sp, prevSp, sendPostMessageToIframeSportsbook) => {
      if (quickBetEnabled && !editBet?.active && !quickBetStake) {
        setSelectedEvents([]);
        showToastError(t('betslip.noStake'));

        document.getElementById('quick-bet-input-stake')?.focus();
      } else {
        if (!selectedEvents.includes(eventId) || sp || prevSp) {
          if (quickBetEnabled && !editBet?.active) {
            if (!shouldShowQuickBetInfoPopover) {
              handleSubmit({ id: eventId, price });
            } else {
              showPopover(eventId, price);
            }
          } else {
            if (
              partnerConfig?.max_selections_in_multiple_bet &&
              allLsBets.length >= partnerConfig?.max_selections_in_multiple_bet
            ) {
              showToastWarning(
                t('betslip.eventsMaxCountMessage').replace(
                  '{%eventsMaxCount%}',
                  `${partnerConfig?.max_selections_in_multiple_bet}`
                )
              );

              return;
            }

            addToBetSlip(eventId, sp);
            sendPostMessageToIframeSportsbook?.();
          }
        } else {
          dispatch(setQuickSingleBetslipOpen(false));
          removeBetSlipEvent(eventId);
          sendPostMessageToIframeSportsbook?.();

          if (shouldShowQuickBetInfoPopover) {
            setPopover(null);
            toggleScroll('enable');
          }
        }

        setSelectedEvents(prevSelected =>
          prevSelected.includes(eventId)
            ? prevSelected.filter(item => item !== eventId)
            : [...prevSelected, eventId]
        );
      }
    },
    [
      shouldShowQuickBetInfoPopover,
      quickBetEnabled,
      quickBetStake,
      editBet,
      partnerConfig,
      allLsBets,
      selectedEvents,
      handleSubmit,
      addToBetSlip,
      showPopover,
      t
    ]
  );

  return useMemo(
    () => ({
      oddChangeHandler,
      popover,
      hidePopover
    }),
    [oddChangeHandler, popover, hidePopover]
  );
};
